import React, { useState, useEffect } from 'react';
import { Card, Table, Switch, Input, Button, Space, Typography, Breadcrumb, message, Alert, Divider, Row, Col } from 'antd';
import { SaveOutlined, SecurityScanOutlined, UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';

import useAuthStore from '../../store/useAuthStore';
import { getUsers, getRolesByUser, assignRoles, getAllModules } from '../../api/users.api';

import SearchableSelect from '../../components/common/SearchableSelect';

const { Title, Text } = Typography;

const UserRolePage = () => {
  const { user } = useAuthStore();
  const plantId = user?.plantID;

  const [selectedUser, setSelectedUser] = useState(null); // { loginID, userName }
  const [roleData, setRoleData] = useState([]); // Local state for table editing
  const [isSaving, setIsSaving] = useState(false);

  // --- Queries ---
  const { data: modules } = useQuery({
    queryKey: ['allModules'],
    queryFn: getAllModules
  });

  // --- Effect: Load User Roles when user is selected ---
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!selectedUser || !modules || modules.length === 0) return;
      
      try {
        const response = await getRolesByUser(plantId, selectedUser.loginID);
        const existingRoles = response || [];
        
        // Map response to table format
        const mergedRoles = existingRoles.map(role => ({
          moduleID: role.modules,
          moduleName: role.moduleName,
          addRecord: role.addRecord || 'N',
          editRecord: role.editRecord || 'N',
          deleteRecord: role.deleteRecord || 'N',
          editPassword: role.editPass || '',
          deletePassword: role.delPass || ''
        }));
        
        setRoleData(mergedRoles);
      } catch (err) {
        message.error("Failed to load user roles");
      }
    };

    fetchUserRoles();
  }, [selectedUser, modules, plantId]);

  // --- Mutation ---
  const saveMutation = useMutation({
    mutationFn: (payload) => assignRoles(plantId, payload),
    onSuccess: () => {
      message.success(`Permissions updated for ${selectedUser.user_Name}`);
      setIsSaving(false);
    },
    onError: (err) => {
      message.error(err?.response?.data?.message || "Failed to save roles");
      setIsSaving(false);
    }
  });

  // --- Handlers ---
  const handleToggle = (moduleId, field, checked) => {
    const val = checked ? 'Y' : 'N';
    setRoleData(prev => prev.map(row => 
      row.moduleID === moduleId ? { ...row, [field]: val } : row
    ));
  };

  const handlePasswordChange = (moduleId, field, value) => {
    setRoleData(prev => prev.map(row => 
      row.moduleID === moduleId ? { ...row, [field]: value } : row
    ));
  };

  const handleSave = () => {
    if (!selectedUser) return;
    setIsSaving(true);
    
    const payload = roleData.map(role => ({
      loginID: selectedUser.loginID,
      modules: role.moduleID,
      addRecord: role.addRecord,
      editRecord: role.editRecord,
      deleteRecord: role.deleteRecord,
      editPass: role.editPassword || null,
      delPass: role.deletePassword || null
    }));

    saveMutation.mutate(payload);
  };

  // --- Table Columns ---
  const columns = [
    { 
      title: 'Module Name', 
      dataIndex: 'moduleName', 
      key: 'moduleName',
      render: (text) => <b>{text}</b>
    },
    { 
      title: 'Can Add', 
      key: 'addRecord', 
      align: 'center',
      render: (_, record) => (
        <Switch 
          checked={record.addRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'addRecord', checked)} 
        />
      )
    },
    { 
      title: 'Can Edit', 
      key: 'editRecord', 
      align: 'center',
      render: (_, record) => (
        <Switch 
          checked={record.editRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'editRecord', checked)} 
        />
      )
    },
    { 
      title: 'Can Delete', 
      key: 'deleteRecord', 
      align: 'center',
      render: (_, record) => (
        <Switch 
          checked={record.deleteRecord === 'Y'} 
          onChange={(checked) => handleToggle(record.moduleID, 'deleteRecord', checked)} 
        />
      )
    },
    { 
      title: 'Edit Password (Optional)', 
      key: 'editPassword',
      render: (_, record) => (
        <Input.Password
          placeholder="Set Pin"
          size="small"
          iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          value={record.editPassword}
          onChange={(e) => handlePasswordChange(record.moduleID, 'editPassword', e.target.value)}
          prefix={<LockOutlined style={{ fontSize: 12, color: '#bfbfbf' }} />}
          disabled={record.editRecord === 'N'}
        />
      )
    },
    { 
      title: 'Delete Password (Optional)', 
      key: 'deletePassword',
      render: (_, record) => (
        <Input.Password
          placeholder="Set Pin"
          size="small"
          iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          value={record.deletePassword}
          onChange={(e) => handlePasswordChange(record.moduleID, 'deletePassword', e.target.value)}
          prefix={<LockOutlined style={{ fontSize: 12, color: '#bfbfbf' }} />}
          disabled={record.deleteRecord === 'N'}
        />
      )
    }
  ];

  return (
    <div>
      <Breadcrumb 
        style={{ marginBottom: 16 }}
        items={[
          { title: 'Home' },
          { title: 'Admin' },
          { title: 'User Roles' },
          ...(selectedUser ? [{ title: selectedUser.user_Name }] : [])
        ]}
      />

      <Card title={<span><SecurityScanOutlined /> Role & Permission Assignment</span>}>
        <Row gutter={24} align="middle">
          <Col xs={24} md={12}>
            <Text strong>Step 1: Select User to Configure</Text>
            <div style={{ marginTop: 8 }}>
              <SearchableSelect
                apiUrl={`/plants/${plantId}/users`}
                valueField="loginID"
                labelField="loginID"
                placeholder="Search user by login ID..."
                onSelect={(u) => setSelectedUser(u)}
                prefix={<UserOutlined />}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
             {selectedUser && (
               <Alert 
                 title={`Configuring permissions for: ${selectedUser.user_Name} (${selectedUser.loginID})`} 
                 type="info" 
                 showIcon 
               />
             )}
          </Col>
        </Row>

        <Divider />

        {selectedUser ? (
          <>
            <Table 
              columns={columns} 
              dataSource={roleData} 
              rowKey="moduleID" 
              pagination={false} 
              size="middle"
              bordered
            />
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                size="large"
                loading={isSaving}
                onClick={handleSave}
              >
                Save All Permissions
              </Button>
            </div>
          </>
        ) : (
          <div style={{ padding: '40px 0', textAlign: 'center', background: '#fafafa', borderRadius: 8 }}>
            <Text type="secondary">Please select a user from the dropdown above to manage their permissions.</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserRolePage;